import { useEffect, useRef, useState } from "react";
import Matter from "matter-js";

type TileId =
  | "BTC"
  | "ETH"
  | "USDT"
  | "USDC"
  | "BNB"
  | "XRP"
  | "SOL"
  | "DOGE"
  | "SHIB"
  | "LTC"
  | "DAI"
  | "BCH";

type TileConfig = {
  id: TileId;
  label: string;
  icon: string;
};

type TileState = {
  id: TileId;
  label: string;
  icon: string;
  x: number;
  y: number;
  angle: number;
};

const TILE_CONFIGS: TileConfig[] = [
  { id: "BTC", label: "BTC", icon: "₿" },
  { id: "ETH", label: "ETH", icon: "◈" },
  { id: "USDT", label: "USDT", icon: "₮" },
  { id: "USDC", label: "USDC", icon: "◎" },
  { id: "BNB", label: "BNB", icon: "⬡" },
  { id: "XRP", label: "XRP", icon: "✕" },
  { id: "SOL", label: "SOL", icon: "◎" },
  { id: "DOGE", label: "DOGE", icon: "Ð" },
  { id: "SHIB", label: "Shiba Inu", icon: "🐕" },
  { id: "LTC", label: "LTC", icon: "Ł" },
  { id: "DAI", label: "DAI", icon: "◍" },
  { id: "BCH", label: "BCH", icon: "Ƀ" },
];

export function DraggableCryptoPairs({
  className = "",
}: {
  className?: string;
}) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const engineRef = useRef<any>(null);
  const runnerRef = useRef<any>(null);
  const bodiesRef = useRef<Record<TileId, any>>({} as Record<TileId, any>);
  const draggingIdRef = useRef<TileId | null>(null);
  const dragOffsetRef = useRef<{ x: number; y: number }>({ x: 0, y: 0 });

  const [tiles, setTiles] = useState<TileState[]>(() =>
    TILE_CONFIGS.map((cfg, index) => ({
      id: cfg.id,
      label: cfg.label,
      icon: cfg.icon,
      x: 120 + index * 80,
      y: 80,
      angle: 0,
    }))
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    const width = rect.width || 600;
    const height = Math.max(rect.height, 220);

    const engine = Matter.Engine.create({
      gravity: { x: 0, y: 1.3 },
    });
    engineRef.current = engine;

    const world = engine.world;

    const wallThickness = 200;
    // Décalage vertical du sol pour garder les tuiles bien visibles au‑dessus de la barre.
    const groundOffset = 40; // plus grand => sol plus haut
    const groundY = height - groundOffset + wallThickness / 2;
    const ground = Matter.Bodies.rectangle(
      width / 2,
      groundY,
      width * 2,
      wallThickness,
      {
        isStatic: true,
        friction: 0.9,
        restitution: 0.1,
      }
    );

    const leftWall = Matter.Bodies.rectangle(
      -wallThickness / 2,
      height / 2,
      wallThickness,
      height * 2,
      { isStatic: true, friction: 0.6, restitution: 0.1 }
    );

    const rightWall = Matter.Bodies.rectangle(
      width + wallThickness / 2,
      height / 2,
      wallThickness,
      height * 2,
      { isStatic: true, friction: 0.6, restitution: 0.1 }
    );

    Matter.Composite.add(world, [ground, leftWall, rightWall]);

    const tileWidth = 100;
    const tileHeight = 44;
    const createdBodies: Record<TileId, any> = {} as Record<TileId, any>;

    TILE_CONFIGS.forEach((cfg, index) => {
      const col = index % 6;
      const row = Math.floor(index / 6);
      const x = 90 + col * (tileWidth + 12);
      const y = 40 + row * (tileHeight + 16);

      const body = Matter.Bodies.rectangle(x, y, tileWidth, tileHeight, {
        restitution: 0.18,
        friction: 0.8,
        frictionStatic: 1.2,
        frictionAir: 0.02,
        chamfer: { radius: 14 },
      });

      Matter.Body.rotate(body, ((index % 3) - 1) * (Math.PI / 14));

      createdBodies[cfg.id] = body;
      Matter.Composite.add(world, body);
    });

    bodiesRef.current = createdBodies;

    const runner = Matter.Runner.create();
    runnerRef.current = runner;
    Matter.Runner.run(runner, engine);

    const syncFromEngine = () => {
      setTiles(
        TILE_CONFIGS.map((cfg) => {
          const body = bodiesRef.current[cfg.id];
          return {
            id: cfg.id,
            label: cfg.label,
            icon: cfg.icon,
            x: body?.position.x ?? 0,
            y: body?.position.y ?? 0,
            angle: body?.angle ?? 0,
          };
        })
      );
    };

    Matter.Events.on(engine, "afterUpdate", syncFromEngine);

    const resizeObserver = new ResizeObserver(() => {
      const r = container.getBoundingClientRect();
      const w = r.width || width;
      const h = Math.max(r.height, height);
      const newGroundY = h - groundOffset + wallThickness / 2;

      Matter.Body.setPosition(ground, { x: w / 2, y: newGroundY });
      Matter.Body.setVertices(
        ground,
        Matter.Vertices.fromPath(
          `0 0 ${w * 2} 0 ${w * 2} ${wallThickness} 0 ${wallThickness}`
        )
      );

      Matter.Body.setPosition(leftWall, {
        x: -wallThickness / 2,
        y: h / 2,
      });
      Matter.Body.setPosition(rightWall, {
        x: w + wallThickness / 2,
        y: h / 2,
      });
    });

    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
      Matter.Events.off(engine, "afterUpdate", syncFromEngine);
      if (runnerRef.current) {
        Matter.Runner.stop(runnerRef.current);
      }
      Matter.Engine.clear(engine);
      engineRef.current = null;
      runnerRef.current = null;
      bodiesRef.current = {} as Record<TileId, any>;
    };
  }, []);

  const handlePointerDown = (e: React.PointerEvent, id: TileId) => {
    const container = containerRef.current;
    const engine = engineRef.current;
    if (!container || !engine) return;

    const rect = container.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    const body = bodiesRef.current[id];
    if (!body) return;

    draggingIdRef.current = id;
    dragOffsetRef.current = {
      x: px - body.position.x,
      y: py - body.position.y,
    };

    Matter.Body.setAngularVelocity(body, 0);
    Matter.Body.setVelocity(body, { x: 0, y: 0 });
    body.frictionAir = 0.12;
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    const id = draggingIdRef.current;
    const container = containerRef.current;
    const engine = engineRef.current;
    if (!id || !container || !engine) return;

    const body = bodiesRef.current[id];
    if (!body) return;

    const rect = container.getBoundingClientRect();
    const px = e.clientX - rect.left;
    const py = e.clientY - rect.top;

    const targetX = px - dragOffsetRef.current.x;
    const targetY = py - dragOffsetRef.current.y;

    Matter.Body.setPosition(body, { x: targetX, y: targetY });
    Matter.Body.setVelocity(body, { x: 0, y: 0 });
    Matter.Body.setAngularVelocity(body, 0);
  };

  const handlePointerUp = () => {
    const id = draggingIdRef.current;
    if (!id) return;
    const body = bodiesRef.current[id];
    if (body) {
      body.frictionAir = 0.02;
    }
    draggingIdRef.current = null;
  };

  return (
    <div
      ref={containerRef}
      className={`synapse-crypto-board ${className}`}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}>
      {tiles.map((t) => (
        <button
          key={t.id}
          type="button"
          className="synapse-crypto-tile"
          onPointerDown={(e) => handlePointerDown(e, t.id)}
          style={{
            transform: `translate3d(${t.x}px, ${t.y}px, 0) rotate(${
              (t.angle * 180) / Math.PI
            }deg)`,
          }}
          aria-label={`Drag ${t.label}`}>
          <span className="synapse-crypto-tile-inner">
            <span className="synapse-crypto-dot" aria-hidden="true" />
            <span className="synapse-crypto-label">
              <span className="synapse-crypto-badge" aria-hidden="true">
                {t.icon}
              </span>
              <span>{t.label}</span>
            </span>
          </span>
        </button>
      ))}
    </div>
  );
}
